import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Globe,
  DollarSign,
  MapPin,
  Users,
  Settings,
  Loader2,
  Search,
  TrendingUp,
  Building,
} from 'lucide-react';

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Dubai',
  'Australia/Sydney',
  'Australia/Melbourne',
];

export default function AdminFranchisesPage() {
  const { api } = useAuth();
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [editingFranchise, setEditingFranchise] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    franchise_type: 'franchise',
    status: 'active',
    region: '',
    country: 'United States',
    country_code: 'US',
    timezone: 'America/Chicago',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    state_province: '',
    postal_code: '',
    royalty_percentage: '0',
    currency: 'USD',
    price_multiplier: '1.00',
    tax_rate: '0.00',
  });

  const [pricingData, setPricingData] = useState({
    currency: 'USD',
    price_multiplier: '1.00',
    tax_rate: '0.00',
    tax_included_in_price: false,
    tax_label: 'Tax',
  });

  useEffect(() => {
    fetchFranchises();
  }, []);

  const fetchFranchises = async () => {
    try {
      setLoading(true);
      const response = await api.get('/franchises/');
      setFranchises(response.data.results || response.data);
    } catch (error) {
      console.error('Failed to fetch franchises:', error);
      toast.error('Failed to load franchises');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (franchise = null) => {
    if (franchise) {
      setEditingFranchise(franchise);
      setFormData({
        name: franchise.name || '',
        code: franchise.code || '',
        franchise_type: franchise.franchise_type || 'franchise',
        status: franchise.status || 'active',
        region: franchise.region || '',
        country: franchise.country || 'United States',
        country_code: franchise.country_code || 'US',
        timezone: franchise.timezone || 'America/Chicago',
        contact_name: franchise.contact_name || '',
        contact_email: franchise.contact_email || '',
        contact_phone: franchise.contact_phone || '',
        address: franchise.address || '',
        city: franchise.city || '',
        state_province: franchise.state_province || '',
        postal_code: franchise.postal_code || '',
        royalty_percentage: franchise.royalty_percentage || '0',
        currency: franchise.pricing?.currency || 'USD',
        price_multiplier: franchise.pricing?.price_multiplier || '1.00',
        tax_rate: franchise.pricing?.tax_rate || '0.00',
      });
    } else {
      setEditingFranchise(null);
      setFormData({
        name: '',
        code: '',
        franchise_type: 'franchise',
        status: 'active',
        region: '',
        country: 'United States',
        country_code: 'US',
        timezone: 'America/Chicago',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        address: '',
        city: '',
        state_province: '',
        postal_code: '',
        royalty_percentage: '0',
        currency: 'USD',
        price_multiplier: '1.00',
        tax_rate: '0.00',
      });
    }
    setDialogOpen(true);
  };

  const handleOpenPricingDialog = (franchise) => {
    setEditingFranchise(franchise);
    setPricingData({
      currency: franchise.pricing?.currency || 'USD',
      price_multiplier: franchise.pricing?.price_multiplier || '1.00',
      tax_rate: franchise.pricing?.tax_rate || '0.00',
      tax_included_in_price: franchise.pricing?.tax_included_in_price || false,
      tax_label: franchise.pricing?.tax_label || 'Tax',
    });
    setPricingDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.code || !formData.contact_email) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingFranchise) {
        await api.put(`/franchises/${editingFranchise.id}/`, formData);
        toast.success('Franchise updated successfully');
      } else {
        await api.post('/franchises/', formData);
        toast.success('Franchise created successfully');
      }
      setDialogOpen(false);
      fetchFranchises();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save franchise');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      await api.post(`/franchises/${editingFranchise.id}/update_pricing/`, pricingData);
      toast.success('Pricing updated successfully');
      setPricingDialogOpen(false);
      fetchFranchises();
    } catch (error) {
      console.error('Pricing save error:', error);
      toast.error('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (franchise) => {
    if (!window.confirm(`Delete franchise "${franchise.name}"? This cannot be undone.`)) return;

    try {
      await api.delete(`/franchises/${franchise.id}/`);
      toast.success('Franchise deleted');
      fetchFranchises();
    } catch (error) {
      toast.error('Failed to delete franchise');
    }
  };

  const filteredFranchises = franchises.filter((f) => {
    const matchesSearch =
      f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.city?.toLowerCase().includes(searchQuery.toLowerCase());

    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'corporate') return matchesSearch && f.franchise_type === 'corporate';
    if (activeTab === 'franchise') return matchesSearch && f.franchise_type === 'franchise';
    if (activeTab === 'international') return matchesSearch && f.country_code !== 'US';
    return matchesSearch;
  });

  const stats = {
    total: franchises.length,
    corporate: franchises.filter((f) => f.franchise_type === 'corporate').length,
    franchise: franchises.filter((f) => f.franchise_type === 'franchise').length,
    international: franchises.filter((f) => f.country_code !== 'US').length,
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-red-100 text-red-800',
      terminated: 'bg-gray-100 text-gray-800',
    };
    return <Badge className={variants[status] || variants.active}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const variants = {
      corporate: 'bg-blue-100 text-blue-800',
      franchise: 'bg-purple-100 text-purple-800',
      affiliate: 'bg-orange-100 text-orange-800',
    };
    return <Badge className={variants[type] || variants.franchise}>{type}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-franchises-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Franchise Management</h1>
          <p className="text-muted-foreground">Manage franchise locations and pricing</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-franchise-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Franchise
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Building2 className="h-8 w-8 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Franchises</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Building className="h-8 w-8 mx-auto mb-2 text-indigo-600" />
            <p className="text-2xl font-bold">{stats.corporate}</p>
            <p className="text-sm text-muted-foreground">Corporate-Owned</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Users className="h-8 w-8 mx-auto mb-2 text-purple-600" />
            <p className="text-2xl font-bold">{stats.franchise}</p>
            <p className="text-sm text-muted-foreground">Franchisees</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 text-center">
            <Globe className="h-8 w-8 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold">{stats.international}</p>
            <p className="text-sm text-muted-foreground">International</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Tabs */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search franchises..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="franchise-search"
          />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="corporate">Corporate ({stats.corporate})</TabsTrigger>
            <TabsTrigger value="franchise">Franchise ({stats.franchise})</TabsTrigger>
            <TabsTrigger value="international">International ({stats.international})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Franchises Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredFranchises.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No franchises found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Franchise</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Locations</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFranchises.map((franchise) => (
                  <TableRow key={franchise.id} data-testid={`franchise-row-${franchise.code}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{franchise.name}</p>
                        <p className="text-sm text-muted-foreground">{franchise.code}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(franchise.franchise_type)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>
                          {franchise.city}, {franchise.country_code}
                        </span>
                        {franchise.country_code !== 'US' && (
                          <Globe className="h-4 w-4 text-green-600 ml-1" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span>{franchise.currency || 'USD'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{franchise.location_count || 0}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(franchise.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenPricingDialog(franchise)}
                          title="Pricing Settings"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(franchise)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(franchise)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Franchise Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFranchise ? 'Edit Franchise' : 'Add New Franchise'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Franchise Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ready Set STEM - Dallas"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Franchise Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="RSC-DAL"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="franchise_type">Type</Label>
                  <Select
                    value={formData.franchise_type}
                    onValueChange={(v) => setFormData({ ...formData, franchise_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="corporate">Corporate-Owned</SelectItem>
                      <SelectItem value="franchise">Franchise</SelectItem>
                      <SelectItem value="affiliate">Affiliate Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending Approval</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Location Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Location
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Dallas"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state_province">State/Province</Label>
                  <Input
                    id="state_province"
                    value={formData.state_province}
                    onChange={(e) => setFormData({ ...formData, state_province: e.target.value })}
                    placeholder="Texas"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="United States"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country_code">Country Code</Label>
                  <Input
                    id="country_code"
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
                    placeholder="US"
                    maxLength={2}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="region">Region/Territory</Label>
                  <Input
                    id="region"
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    placeholder="North Texas"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(v) => setFormData({ ...formData, timezone: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="John Smith"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>

            {/* Financial */}
            {!editingFranchise && (
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Financial Settings
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(v) => setFormData({ ...formData, currency: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.symbol} {c.code} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="price_multiplier">Price Multiplier</Label>
                    <Input
                      id="price_multiplier"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={formData.price_multiplier}
                      onChange={(e) => setFormData({ ...formData, price_multiplier: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">1.0 = base price, 0.8 = 20% less</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="royalty_percentage">Royalty %</Label>
                    <Input
                      id="royalty_percentage"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.royalty_percentage}
                      onChange={(e) => setFormData({ ...formData, royalty_percentage: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingFranchise ? 'Save Changes' : 'Create Franchise'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Settings Dialog */}
      <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Pricing Settings
              </div>
            </DialogTitle>
            {editingFranchise && (
              <p className="text-sm text-muted-foreground">{editingFranchise.name}</p>
            )}
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="p_currency">Currency</Label>
              <Select
                value={pricingData.currency}
                onValueChange={(v) => setPricingData({ ...pricingData, currency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.symbol} {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="p_multiplier">Price Multiplier</Label>
              <Input
                id="p_multiplier"
                type="number"
                step="0.01"
                min="0.01"
                value={pricingData.price_multiplier}
                onChange={(e) => setPricingData({ ...pricingData, price_multiplier: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Multiply base USD price. Example: $100 USD × {pricingData.price_multiplier} ={' '}
                {CURRENCIES.find((c) => c.code === pricingData.currency)?.symbol}
                {(100 * parseFloat(pricingData.price_multiplier || 1)).toFixed(2)}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="p_tax">Tax Rate (%)</Label>
              <Input
                id="p_tax"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={pricingData.tax_rate}
                onChange={(e) => setPricingData({ ...pricingData, tax_rate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="p_tax_label">Tax Label</Label>
              <Input
                id="p_tax_label"
                value={pricingData.tax_label}
                onChange={(e) => setPricingData({ ...pricingData, tax_label: e.target.value })}
                placeholder="VAT, GST, Sales Tax"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="p_tax_included"
                checked={pricingData.tax_included_in_price}
                onChange={(e) =>
                  setPricingData({ ...pricingData, tax_included_in_price: e.target.checked })
                }
                className="rounded"
              />
              <Label htmlFor="p_tax_included" className="text-sm">
                Tax included in displayed prices
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPricingDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePricing} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Pricing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
